/**
 * HTTP adapter for the enrollment + waitlist endpoints.
 *
 * Matches the backend as built (backend/enroll/router.py, enroll/models.py, waitlist/router.py); UNEXECUTED
 * against a running server. The form produces a rich payload (consent_events etc.) that the mock and tests use;
 * toServerPayload() reduces it to the strict EnrollmentSubmission the server accepts (extra fields are forbidden).
 *
 *   GET  /api/enrollments/config?plan_id=<id>&state=<code>
 *        -> { connector_slug, energy_type, plan_rate_snapshot_id, consent_texts, disclosure, privacy_version_id,
 *             supplier_documents, extension_fields: ExtensionField[] }
 *   POST /api/enrollments/starts     body: { plan_id, state, plan_rate_snapshot_id, start_key? }  (no personal data)
 *        -> 201/200 { start_key, started_at, idempotent_replay }
 *   POST /api/enrollments            body: EnrollmentSubmission (carries start_key when the form opened a start)
 *        -> 200/201 { enrollment_id, status, next_step, created_at, idempotent_replay }
 *        422 pydantic list (detail[].loc) or { detail: { field_errors: { "<dotted.field>": "type" } } }
 *   POST /api/waitlist               body: { email, state, track } -> 202 { ok: true }
 *
 * ExtensionField: { name, label, type: "text"|"tel"|"number"|"date"|"select", required,
 *                   help?, pattern?, pattern_message?, options?: [{value,label}], maxLength? }
 */
import { api } from "../api";

export class EnrollmentApiError extends Error {
  /**
   * kind:
   *   validation    - server rejected field data (4xx with field_errors); user can fix and resubmit
   *   rejected      - other 4xx; do not blindly retry
   *   rate_changed  - 409: the rate or documents shown are stale; reload, show the new rate, re-confirm before resubmitting
   *   rate_limited  - 429: too many attempts (Retry-After says how long); the request was NOT processed
   *   unavailable   - endpoint missing/unreachable before the server acted (404, 501, 503)
   *   indeterminate - timeout / dropped connection / 5xx: the server MAY have acted. Never call this "failed".
   */
  constructor(kind, message, fieldErrors = {}) {
    super(message);
    this.name = "EnrollmentApiError";
    this.kind = kind;
    this.fieldErrors = fieldErrors;
  }
}

/**
 * Customer-language copy for a server field error (QA-18). Pydantic error types ("missing", "value_error",
 * "string_too_short", "extra_forbidden") and pydantic's developer messages never reach the screen: a missing value reads
 * "Enter this field.", anything else "Check this field.". A field_errors value the server wrote for a customer (a full
 * sentence) may pass through; a bare code-shaped token may not.
 */
export function fieldMessage(value, type) {
  if (type === "missing" || value === "missing") return "Enter this field.";
  const text = typeof value === "string" ? value.trim() : "";
  if (/^[A-Z][^_]*\s[^_]*[.!?]$/.test(text)) return text;
  return "Check this field.";
}

/** FastAPI/pydantic 422 detail is a list of { loc: ["body", ...path], type }; map to dotted form-field names. */
function fieldErrorsFrom(detail) {
  if (!detail) return {};
  if (Array.isArray(detail)) {
    const out = {};
    detail.forEach((d) => {
      const path = (d.loc || []).filter((p) => p !== "body").join(".");
      // Only the error TYPE decides the wording; pydantic's own messages are English for developers.
      if (path) out[path] = fieldMessage(null, d.type);
    });
    return out;
  }
  const fe = detail.field_errors || {};
  return Object.fromEntries(Object.entries(fe).map(([k, v]) => [k, fieldMessage(v, v)]));
}

/** 409 codes that mean "the rate or documents you were shown are no longer current" (QA-17). */
const RATE_CHANGED_CODES = /^(PLAN_RATE_CHANGED|SNAPSHOT_[A-Z_]+|SUPPLIER_DOCS_HASH_MISMATCH)$/;

/** Reduce the form's payload to the exact shape backend/enroll/models.py EnrollmentSubmission accepts. */
export function toServerPayload(p) {
  const events = p.consent_events || [];
  const ev = (t) => events.find((e) => e.consent_type === t);
  const c = p.consent || {};
  const tos = ev("tos");
  const share = ev("data_share");
  const sms = ev("sms");
  return {
    first_name: p.first_name,
    last_name: p.last_name,
    email: p.email,
    phone: p.phone,
    service_address: { ...p.service_address, unit: p.service_address.unit || null },
    energy_type: p.energy_type,
    customer_type: p.customer_type,
    provider_id: p.provider_id,
    plan_id: p.plan_id,
    plan_rate_snapshot_id: p.plan_rate_snapshot_id,
    displayed_at: p.displayed_at,
    disclosure_version_id: p.disclosure_version_id,
    preferred_start_date: p.preferred_start_date || null,
    consent: {
      tos_accepted_at: c.tos_accepted_at,
      tos_text_version_id: tos && tos.text_version_id,
      privacy_version_id: p.privacy_version_id,
      data_share_consent_at: c.data_share_consent_at,
      data_share_text_version_id: share && share.text_version_id,
      data_share_provider_id: p.provider_id,
      supplier_docs_acknowledged_at: c.supplier_docs_acknowledged_at,
      supplier_doc_hashes: p.supplier_doc_hashes || [],
      authority_attested_at: c.authority_attested_at,
      sms_consent_at: c.sms_consent_at || null,
      sms_text_version_id: c.sms_consent_at && sms ? sms.text_version_id : null,
      capture: { page_url: tos ? tos.page_url || null : null },
    },
    supplier_extension: p.supplier_extension || {},
    submission_idempotency_key: p.submission_idempotency_key,
    // Links this submission to the form-open record (reporting). Omitted when there is none: the server treats
    // a missing or malformed key as absent and never blocks a submission on it.
    ...(p.start_key ? { start_key: p.start_key } : {}),
  };
}

function toApiError(err) {
  if (err instanceof EnrollmentApiError) return err;
  const res = err && err.response;
  if (!res) return new EnrollmentApiError("indeterminate", "No response from server");
  const status = res.status;
  const detail = res.data && res.data.detail;
  const fieldErrors = fieldErrorsFrom(detail);
  const code = detail && typeof detail === "object" && !Array.isArray(detail) ? detail.code : null;
  if (status === 409 && typeof code === "string" && RATE_CHANGED_CODES.test(code)) return new EnrollmentApiError("rate_changed", "HTTP 409");
  if (status === 429) return new EnrollmentApiError("rate_limited", "HTTP 429");
  if (status === 404 || status === 501 || status === 503) return new EnrollmentApiError("unavailable", `HTTP ${status}`);
  if (status >= 500) return new EnrollmentApiError("indeterminate", `HTTP ${status}`);
  if (status === 422 || status === 400) return new EnrollmentApiError("validation", `HTTP ${status}`, fieldErrors);
  return new EnrollmentApiError("rejected", `HTTP ${status}`);
}

export const httpEnrollmentApi = {
  isMock: false,

  async getEnrollmentConfig(planId, stateCode) {
    try {
      const r = await api.get("/enrollments/config", { params: { plan_id: planId, state: stateCode }, timeout: 15000 });
      return r.data;
    } catch (e) {
      throw toApiError(e);
    }
  },

  /**
   * Record that the enrollment form opened (backend/reporting/router.py). No personal data is sent: the body is
   * exactly { plan_id, state, plan_rate_snapshot_id, start_key } (the server forbids extra fields).
   * 201 new start, 200 replay of the same key. Returns { start_key, started_at, idempotent_replay }.
   * Callers treat this as fire-and-forget: a failure here must never block or fail the form.
   */
  async startEnrollment({ planId, stateCode, planRateSnapshotId, startKey }) {
    try {
      const body = { plan_id: planId, state: stateCode, plan_rate_snapshot_id: planRateSnapshotId };
      if (startKey) body.start_key = startKey;
      const r = await api.post("/enrollments/starts", body, { timeout: 10000 });
      return r.data;
    } catch (e) {
      throw toApiError(e);
    }
  },

  async submitEnrollment(payload) {
    try {
      const r = await api.post("/enrollments", toServerPayload(payload), { timeout: 30000 });
      return r.data;
    } catch (e) {
      throw toApiError(e);
    }
  },

  async joinWaitlist({ stateCode, track, email }) {
    try {
      await api.post("/waitlist", { state: stateCode, track, email }, { timeout: 15000 });
    } catch (e) {
      throw toApiError(e);
    }
  },
};
